/**
 * LoansPage — Phase 8 Loans / Borrowings module.
 * List + detail tabs (Overview / Schedule / Payments). Finance Manager,
 * Senior Accountant, and Admin can disburse / record payment / payoff;
 * Accountant + Auditor + Observer are read-only.
 */
import { useEffect, useState, useCallback, Fragment } from "react";
import BackToDashBar from "@/components/BackToDashBar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Banknote, ArrowLeft, FileBarChart2, Coins, ShieldCheck, Pencil, Trash2, ChevronRight, ChevronDown, CalendarRange, PlayCircle, AlertTriangle, Receipt, TrendingUp } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RecordTrail from "@/components/RecordTrail";

const ROLES_WRITE = ["system_admin", "salihi_senior_accountant", "salihi_finance_manager"];
const fmt = (v) => `$${Number(v||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
// Phase 27-c (2026-07-16) — human-readable date format for loan pills.
const fmtLoanDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const d = new Date(s + "T00:00:00Z");
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700",
  active: "bg-sky-100 text-sky-700",
  paid_off: "bg-emerald-100 text-emerald-700",
  defaulted: "bg-red-100 text-red-700",
  restructured: "bg-amber-100 text-amber-700",
  voided: "bg-slate-200 text-slate-500 line-through",
};

export default function LoansPage() {
  const { user, can } = useAuth();
  // Phase 19 PR-2 hot-fix (2026-07-06) — permission-key gate.
  const canCreate = can ? can("accounting.loans.create") : false;
  const canWrite = canCreate || (can ? (can("accounting.loans.payment") || can("accounting.loans.approve")) : false);
  // P1 (2026-07-14) — Edit + Delete permissions.
  const canEdit = can ? can("accounting.loans.edit") : false;
  const canDelete = can ? can("accounting.loans.delete") : false;
  const canReverseJEs = can ? can("accounting.loans.reverse_jes") : false;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editLoan, setEditLoan] = useState(null);
  const [deleteLoan, setDeleteLoan] = useState(null);
  // Bug bundle #2 (2026-08-10) — Rebuild Schedule modal state. Admin-
  // only. Opens a small form that lets ops re-anchor a loan's schedule
  // to its actual disbursement date without touching any JE.
  const isAdmin = user?.role === "system_admin" || user?.full_access === true;
  const [rebuildLoan, setRebuildLoan] = useState(null);
  // Phase E hot-fix (2026-10-10) — portfolio-wide IAS 39 rebuild modal.
  const [bulkRebuildOpen, setBulkRebuildOpen] = useState(false);
  // Phase D (2026-10-04) — pending interest accrual badge + Run Now button.
  const [pendingAccrual, setPendingAccrual] = useState({ count: 0, total_interest: 0 });
  const [sweepBusy, setSweepBusy] = useState(false);
  const [confirmSweep, setConfirmSweep] = useState(false);
  const [accrualSummary, setAccrualSummary] = useState({});
  const refreshPending = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/loans/accrual-sweep/pending"),
        api.get("/loans/accrual-sweep/summary"),
      ]);
      setPendingAccrual({ count: p.data.count, total_interest: p.data.total_interest });
      setAccrualSummary(s.data || {});
    } catch { /* keep stale */ }
  }, []);

  const runSweep = async () => {
    setSweepBusy(true);
    try {
      const { data } = await api.post("/loans/accrual-sweep/run");
      toast.success(
        `${data.jes_created} interest accrual JE${data.jes_created === 1 ? "" : "s"} posted ` +
        `($${Number(data.total_interest).toFixed(2)} total). ${data.rows_promoted} rows promoted.`,
      );
      setConfirmSweep(false);
      await Promise.all([load(), refreshPending()]);
    } catch (e) {
      toast.error(formatApiError(e) || "Accrual sweep failed.");
    } finally { setSweepBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/loans");
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); refreshPending(); }, [load, refreshPending]);

  const openDetail = async (loanId) => {
    try {
      const { data } = await api.get(`/loans/${loanId}`);
      setSelected(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#4FA3D1]" /></div>;

  if (selected) return (
    <LoanDetail
      loan={selected} canWrite={canWrite}
      onBack={() => { setSelected(null); load(); }}
      onRefresh={async () => { const { data } = await api.get(`/loans/${selected.id}`); setSelected(data); }}
    />
  );

  return (
    <div className="space-y-5" data-testid="loans-page">
      <BackToDashBar />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">Loans & Borrowings</h2>
          <p className="text-sm text-slate-500 mt-1">Track loan balances, amortization schedules, and installment payments. Each payment posts a balanced journal entry (Dr Loan Payable + Dr Interest Expense / Cr Bank).</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && pendingAccrual.count > 0 && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm flex items-center gap-2"
              data-testid="pending-accrual-badge"
            >
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              <span className="text-amber-800">
                <span className="font-semibold">${Number(pendingAccrual.total_interest).toFixed(2)}</span>
                {" "}pending accrual · {pendingAccrual.count} row{pendingAccrual.count === 1 ? "" : "s"}
              </span>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-400 text-amber-800 hover:bg-amber-100 h-7 px-2 ml-1"
                onClick={() => setConfirmSweep(true)}
                data-testid="run-accrual-now-btn"
              >
                <PlayCircle className="h-3.5 w-3.5 mr-1" /> Run now
              </Button>
            </div>
          )}
          {isAdmin && pendingAccrual.count === 0 && (
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="accrual-up-to-date-badge">
              ✓ Interest accrual up to date
            </Badge>
          )}
          {/* Phase E hot-fix (2026-10-10) — portfolio-wide rebuild. */}
          {isAdmin && (
            <Button
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-50 h-9"
              onClick={() => setBulkRebuildOpen(true)}
              data-testid="rebuild-all-accruals-btn"
              title="Rebuild every loan's accruals using IAS 39 Actual/365"
            >
              <AlertTriangle className="h-4 w-4 mr-1.5" />
              Rebuild ALL loan accruals (IAS 39)
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => setShowCreate(true)} className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white" data-testid="new-loan-button">
              <Coins className="h-4 w-4 mr-1.5" /> New loan
            </Button>
          )}
        </div>
      </div>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Loan No.</TableHead><TableHead>Lender</TableHead>
            <TableHead className="text-right">Principal</TableHead>
            <TableHead className="text-right">Outstanding</TableHead>
            <TableHead>Rate</TableHead><TableHead>Status</TableHead>
            <TableHead>Last accrued through</TableHead>
            <TableHead>Next payment</TableHead>
            {(canEdit || canDelete || isAdmin) && <TableHead className="text-right">Actions</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 ? <TableRow><TableCell colSpan={(canEdit || canDelete || isAdmin) ? 9 : 8} className="text-center py-8 text-slate-400">No loans yet.</TableCell></TableRow>
            : items.map((l) => (
                <TableRow
                  key={l.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => setSelected(l)}
                  data-testid={`loan-row-${l.loan_number}`}
                >
                  <TableCell className="font-medium">{l.loan_number}</TableCell>
                  <TableCell>{l.lender_name}<span className="text-xs text-slate-400 block">{l.lender_type}</span></TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(l.principal_amount)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{fmt(l.outstanding_principal)}</TableCell>
                  <TableCell>
                    <span>{l.interest_rate}%</span>
                    {/* Phase F (2026-10-10) — day-count convention pill.
                        Phase F.2 (2026-10-10) — adds COMPOUND_ACT_365 (CPD/EIR) badge. */}
                    <Badge
                      variant="outline"
                      className={"ml-1.5 text-[10px] py-0 px-1.5 h-5 font-medium " + (
                        (l.day_count_convention || "ACT_365") === "COMPOUND_ACT_365" ? "border-emerald-400 bg-emerald-50 text-emerald-800" :
                        (l.day_count_convention || "ACT_365") === "_30_360" ? "border-amber-300 bg-amber-50 text-amber-800" :
                        (l.day_count_convention || "ACT_365") === "_30_360_US" ? "border-orange-300 bg-orange-50 text-orange-800" :
                        (l.day_count_convention || "ACT_365") === "ACT_360" ? "border-slate-300 bg-slate-50 text-slate-700" :
                        "border-sky-300 bg-sky-50 text-sky-800")}
                      title={"Day-count: " + (
                        (l.day_count_convention || "ACT_365") === "COMPOUND_ACT_365" ? "Compound Actual / 365 (IAS 39 EIR — Deloitte audit template convention)" :
                        (l.day_count_convention || "ACT_365") === "_30_360" ? "30/360 (Turkish / corporate)" :
                        (l.day_count_convention || "ACT_365") === "_30_360_US" ? "30/360 US (NASD)" :
                        (l.day_count_convention || "ACT_365") === "ACT_360" ? "Actual / 360" :
                        "Actual / 365 (IAS 39 simple)")}
                      data-testid={`loan-conv-${l.loan_number}`}
                    >
                      {(l.day_count_convention || "ACT_365") === "COMPOUND_ACT_365" ? "EIR" :
                       (l.day_count_convention || "ACT_365") === "_30_360" ? "30/360" :
                       (l.day_count_convention || "ACT_365") === "_30_360_US" ? "30/360-US" :
                       (l.day_count_convention || "ACT_365") === "ACT_360" ? "A/360" :
                       "A/365"}
                    </Badge>
                  </TableCell>
                  <TableCell><Badge className={STATUS_STYLE[l.status] || ""}>{l.status}</Badge></TableCell>
                  <TableCell className="text-xs" data-testid={`loan-last-accrued-${l.loan_number}`}>
                    {accrualSummary[l.id]?.last_accrued_through
                      ? fmtLoanDate(accrualSummary[l.id].last_accrued_through)
                      : (l.last_accrued_through ? fmtLoanDate(l.last_accrued_through) : "—")}
                  </TableCell>
                  <TableCell className="text-xs">{l.next_payment_date ? `${l.next_payment_date} · ${fmt(l.next_payment_amount)}` : "—"}</TableCell>
                  {(canEdit || canDelete || isAdmin) && (
                    <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {canEdit && l.status !== "voided" && (
                        <button
                          type="button"
                          onClick={() => setEditLoan(l)}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100 text-slate-600"
                          title="Edit loan"
                          data-testid={`loan-edit-${l.loan_number}`}
                        ><Pencil className="h-4 w-4" /></button>
                      )}
                      {/* Bug bundle #2 (2026-08-10) — Rebuild schedule.
                          Admin-only. Opens a modal that re-anchors the
                          loan's schedule to its actual disbursement date.
                          Never touches JEs. */}
                      {isAdmin && l.status !== "voided" && (
                        <button
                          type="button"
                          onClick={() => setRebuildLoan(l)}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-sky-50 text-sky-700 ml-1"
                          title="Rebuild schedule"
                          data-testid={`loan-rebuild-${l.loan_number}`}
                        ><CalendarRange className="h-4 w-4" /></button>
                      )}
                      {canDelete && l.status !== "voided" && (
                        <button
                          type="button"
                          onClick={() => setDeleteLoan(l)}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-red-50 text-red-600 ml-1"
                          title={l.status === "draft" ? "Delete loan" : "Void loan"}
                          data-testid={`loan-delete-${l.loan_number}`}
                        ><Trash2 className="h-4 w-4" /></button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {showCreate && <CreateLoanDialog open={showCreate} onOpenChange={setShowCreate} onCreated={load} />}
      {editLoan && (
        <CreateLoanDialog
          open={!!editLoan}
          onOpenChange={(o) => !o && setEditLoan(null)}
          initial={editLoan}
          onCreated={() => { setEditLoan(null); load(); toast.info("Loan updated. Refresh the Dashboard to see updated outstanding loans."); }}
        />
      )}
      {deleteLoan && (
        <DeleteLoanDialog
          loan={deleteLoan}
          canReverseJEs={canReverseJEs}
          onClose={() => setDeleteLoan(null)}
          onDone={() => { setDeleteLoan(null); load(); toast.info("Refresh the Dashboard to see updated outstanding loans."); }}
        />
      )}
      {rebuildLoan && (
        <RebuildScheduleDialog
          loan={rebuildLoan}
          onClose={() => setRebuildLoan(null)}
          onDone={(newNext) => {
            setRebuildLoan(null);
            load();
            toast.success(`Schedule rebuilt. Next payment: ${newNext || "—"}`);
          }}
        />
      )}
      {/* Phase E hot-fix (2026-10-10) — portfolio-wide IAS 39 rebuild. */}
      {bulkRebuildOpen && (
        <RebuildAllAccrualsDialog
          onCancel={() => setBulkRebuildOpen(false)}
          onSuccess={() => { setBulkRebuildOpen(false); load(); }}
        />
      )}
      {/* Phase D (2026-10-04) — confirm the manual accrual sweep. */}
      {confirmSweep && (
        <Dialog open={confirmSweep} onOpenChange={(o) => !o && setConfirmSweep(false)}>
          <DialogContent data-testid="confirm-accrual-sweep-dialog">
            <DialogHeader>
              <DialogTitle>Run interest accrual now?</DialogTitle>
            </DialogHeader>
            <div className="text-sm text-slate-600 space-y-2">
              <p>This will post <span className="font-semibold">{pendingAccrual.count}</span> balanced JE(s)
                 (Dr 5300 Interest Expense / Cr 2160 Interest Payable) totalling{" "}
                 <span className="font-semibold">${Number(pendingAccrual.total_interest).toFixed(2)}</span>.</p>
              <p className="text-xs">Each JE links back to its source schedule row. Running this twice is safe — the engine is idempotent.</p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setConfirmSweep(false)} disabled={sweepBusy} data-testid="confirm-sweep-cancel">
                Cancel
              </Button>
              <Button onClick={runSweep} disabled={sweepBusy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-sweep-run">
                {sweepBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PlayCircle className="h-4 w-4 mr-1" />}
                Post {pendingAccrual.count} accrual JE(s)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Bug bundle #2 (2026-08-10) — Rebuild loan schedule dialog. Admin-only
// UI wrapper over `POST /api/loans/{id}/rebuild-schedule`. Captures the
// anchor date + a required audit reason. Never touches JEs.
function RebuildScheduleDialog({ loan, onClose, onDone }) {
  const [anchorDate, setAnchorDate] = useState(
    loan.disbursement_date || loan.start_date || "",
  );
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const reasonTrim = reason.trim();
  const canSubmit =
    !!anchorDate && reasonTrim.length >= 10 && !busy;

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      const { data } = await api.post(`/loans/${loan.id}/rebuild-schedule`, {
        anchor_date: anchorDate,
        reason: reasonTrim,
      });
      onDone(data?.next_payment_date);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="rebuild-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-sky-700" />
            Rebuild loan schedule
          </DialogTitle>
        </DialogHeader>

        {/* Read-only loan summary */}
        <div className="grid grid-cols-2 gap-2 rounded border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="rebuild-summary">
          <div><span className="text-slate-500">Loan No.</span><div className="font-semibold text-slate-800">{loan.loan_number}</div></div>
          <div><span className="text-slate-500">Lender</span><div className="font-semibold text-slate-800 truncate">{loan.lender_name}</div></div>
          <div><span className="text-slate-500">Principal</span><div className="font-semibold text-slate-800 tabular-nums">{fmt(loan.principal_amount)}</div></div>
          <div><span className="text-slate-500">Current start_date</span><div className="font-mono text-slate-800">{loan.start_date || "—"}</div></div>
          <div><span className="text-slate-500">Current disbursement_date</span><div className="font-mono text-slate-800">{loan.disbursement_date || "—"}</div></div>
          <div><span className="text-slate-500">Current next payment</span><div className="font-mono text-slate-800">{loan.next_payment_date || "—"}</div></div>
        </div>

        {/* Warning */}
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-xs" data-testid="rebuild-warning">
          This will regenerate the entire amortization schedule from the anchor date.
          Existing posted JEs are NOT touched. Reversible only by rebuilding again.
        </div>

        {/* Anchor date */}
        <div className="mt-3">
          <Label className="text-xs">Anchor date</Label>
          <Input
            type="date"
            value={anchorDate}
            onChange={(e) => setAnchorDate(e.target.value)}
            data-testid="rebuild-anchor-date"
          />
          <p className="mt-1 text-xs text-slate-500">
            Defaults to the loan&apos;s disbursement_date (or start_date if not set).
          </p>
        </div>

        {/* Reason (required, min 10 chars) */}
        <div className="mt-3">
          <Label className="text-xs">Reason <span className="text-red-500">*</span></Label>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Loan actually disbursed on Aug 1, not the facility start date of May 6."
            data-testid="rebuild-reason"
          />
          <p className="mt-1 text-xs text-slate-500">
            Minimum 10 characters. Captured on the audit log.
            {reasonTrim && reasonTrim.length < 10 && (
              <span className="text-amber-600"> ({10 - reasonTrim.length} more needed)</span>
            )}
          </p>
        </div>

        {err && (
          <div className="mt-3 rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-xs" data-testid="rebuild-error">
            {err}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid="rebuild-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
            data-testid="rebuild-submit"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            Rebuild schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LoanDetail({ loan, canWrite, onBack, onRefresh }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "system_admin" || user?.full_access === true;
  const [payInst, setPayInst] = useState(null);
  const [editDisb, setEditDisb] = useState(false);
  const [rebuildOpen, setRebuildOpen] = useState(false);
  // Phase F (2026-10-10) — day-count convention edit dialog.
  const [dayCountOpen, setDayCountOpen] = useState(false);
  const [interestStatus, setInterestStatus] = useState(null);
  const isActive = loan.status === "active";
  // Phase 27-b (2026-07-15) — fetch the live schedule computed from
  // disbursements so a 900k facility with 100k drawn shows a 100k
  // amortization, not 900k.
  const [scheduleData, setScheduleData] = useState({
    schedule: [], breakdown_by_row: [], note: null, accrual_mode: null,
  });
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setScheduleLoading(true);
    api.get(`/loans/${loan.id}/schedule`)
      .then(({ data }) => { if (!cancelled) setScheduleData(data); })
      .catch(() => { if (!cancelled) setScheduleData({ schedule: [], breakdown_by_row: [], note: null }); })
      .finally(() => { if (!cancelled) setScheduleLoading(false); });
    // Phase D — Interest Status card
    api.get(`/loans/${loan.id}/interest-status`)
      .then(({ data }) => { if (!cancelled) setInterestStatus(data); })
      .catch(() => { if (!cancelled) setInterestStatus(null); });
    return () => { cancelled = true; };
  }, [loan.id, loan.total_drawn, loan.outstanding_principal]);
  const breakdownByDate = (scheduleData.breakdown_by_row || []).reduce((acc, r) => {
    acc[r.due_date] = r.tranches; return acc;
  }, {});

  const recordPayment = async (inst, { amount, payment_date, bank_account_code, memo }) => {
    try {
      // Phase D (2026-10-04) — route through the new draft-JE endpoint so
      // interest payments land in the standard Create → Verify → Post
      // approval pipeline instead of auto-posting.
      await api.post(
        `/loans/${loan.id}/schedule/${inst.installment_no}/record-payment`,
        { amount: Number(amount), payment_date, bank_account_code, memo },
      );
      toast.success(
        `Draft JE created for installment ${inst.installment_no} — ` +
        `pending approval on the Journal Entries page.`,
      );
      setPayInst(null);
      await onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const disburse = async () => {
    try {
      // Phase 52-B (2026-08-06) — prefer the bank_account_code the user
      // selected on the New Loan form; only fall back to 1010 for legacy
      // loans that don't carry one.
      const bank_account_code = loan.bank_account_code || "1010";
      await api.post(`/loans/${loan.id}/disburse`, { bank_account_code });
      toast.success("Loan disbursed — JE posted (Dr Bank / Cr Loan Payable).");
      await onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const payoff = async () => {
    if (!window.confirm(`Pay off remaining ${fmt(loan.outstanding_principal)} principal? This posts a balanced JE (Dr Loan Payable / Cr Bank) and marks all scheduled installments paid.`)) return;
    try {
      const bank_account_code = loan.bank_account_code || "1010";
      await api.post(`/loans/${loan.id}/payoff`, { bank_account_code });
      toast.success("Loan paid off — JE posted.");
      await onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="loan-detail">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="loan-back-button">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
        </Button>
        <div>
          <h2 className="text-xl font-bold">{loan.loan_number} · {loan.lender_name}</h2>
          <p className="text-sm text-slate-500">{loan.lender_type} · {loan.term_months}-month {loan.interest_type.replace("_", " ")} · {loan.interest_rate}% annual</p>
        </div>
        <Badge className={`ml-auto ${STATUS_STYLE[loan.status] || ""}`}>{loan.status}</Badge>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="disbursements" data-testid="tab-disbursements">Disbursements</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Trail</TabsTrigger>
        </TabsList>

        <TabsContent value="disbursements" className="mt-4 space-y-4">
          <DisbursementsTab loan={loan} canWrite={canWrite} onRefresh={onRefresh} />
        </TabsContent>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid sm:grid-cols-4 gap-3">
            <KPI label="Principal" value={fmt(loan.principal_amount)} />
            <KPI label="Outstanding" value={fmt(loan.outstanding_principal)} accent="text-[#E63946]" />
            <KPI label="Interest paid YTD" value={fmt(loan.total_interest_paid)} />
            <KPI label="Next payment" value={loan.next_payment_date ? `${loan.next_payment_date}` : "—"} sub={loan.next_payment_date ? fmt(loan.next_payment_amount) : ""} />
          </div>
          {/* Phase 27-c (2026-07-16) — Loan Year window pill + Recorded date */}
          {(loan.start_date && (loan.maturity_date || loan.end_date)) && (
            <div className="flex flex-wrap gap-3 items-center" data-testid="loan-year-block">
              <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-sm" data-testid="loan-year-pill">
                <span className="text-slate-500 mr-2">Loan Year:</span>
                <span className="font-semibold">{fmtLoanDate(loan.start_date)}</span>
                <span className="mx-2 text-slate-400">→</span>
                <span className="font-semibold">{fmtLoanDate(loan.maturity_date || loan.end_date)}</span>
              </div>
              {loan.recording_date && (
                <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-sm" data-testid="loan-recorded-pill">
                  <span className="text-slate-500 mr-2">Recorded:</span>
                  <span className="font-semibold">{fmtLoanDate(loan.recording_date)}</span>
                </div>
              )}
            </div>
          )}
          {canWrite && loan.status === "draft" && (
            <Button onClick={disburse} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="disburse-btn">
              <Banknote className="h-4 w-4 mr-1.5" /> Disburse loan now
            </Button>
          )}
          {canWrite && isActive && Number(loan.outstanding_principal||0) > 0 && (
            <Button onClick={payoff} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50 ml-2" data-testid="payoff-btn">
              <ShieldCheck className="h-4 w-4 mr-1.5" /> Pay off remaining balance
            </Button>
          )}
          {isAdmin && (
            <Button onClick={() => setEditDisb(true)} variant="outline" className="ml-2" data-testid="edit-disbursement-date-btn">
              <CalendarRange className="h-4 w-4 mr-1.5" /> Edit disbursement date
            </Button>
          )}

          {/* Phase D (2026-10-04) — Interest Status card */}
          <Card className="border-sky-200" data-testid="interest-status-card">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-sky-600" />
                <CardTitle className="text-base">Interest Status</CardTitle>
              </div>
              <CardDescription>Auto-updates after each accrual sweep.</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              {!interestStatus ? (
                <div className="text-sm text-slate-500">Loading…</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  <KPI label="Accrued to date" value={fmt(interestStatus.accrued_to_date)}
                       accent="text-slate-900" />
                  <KPI label="Paid to date" value={fmt(interestStatus.paid_to_date)}
                       accent="text-emerald-700" />
                  <KPI label="Outstanding (unpaid)" value={fmt(interestStatus.outstanding)}
                       accent="text-[#E63946]" />
                  <KPI label="Next accrual due"
                       value={interestStatus.next_accrual_due
                                ? fmtLoanDate(interestStatus.next_accrual_due) : "—"} />
                  <KPI label="Last accrued through"
                       value={interestStatus.last_accrued_through
                                ? fmtLoanDate(interestStatus.last_accrued_through) : "—"}
                       sub={interestStatus.pending_candidates > 0
                              ? `⚠ ${interestStatus.pending_candidates} row(s) overdue` : ""} />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Phase E (2026-10-04) — IAS 39 Rebuild card (admin only) */}
          {isAdmin && (
            <Card className="border-rose-200 bg-rose-50/40" data-testid="rebuild-ias39-card">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-rose-700" />
                  <CardTitle className="text-base text-rose-900">
                    Rebuild accruals (IAS 39)
                  </CardTitle>
                </div>
                <CardDescription className="text-xs text-rose-800/80">
                  Replaces calendar-month accruals with Actual/365 effective-interest math
                  calculated from the loan's actual disbursement date. Soft-voids
                  historical <code>auto_accrual</code> and <code>auto_accrual_seed</code> JEs
                  (invisible in reports, preserved in Audit Log) and posts fresh IAS 39 JEs.
                  Requires MFA step-up.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs text-rose-900/80">
                    <div>Disbursement date on file:{" "}
                      <span className="font-semibold">
                        {loan.disbursement_date || loan.start_date || "—"}
                      </span>
                    </div>
                    <div>Day-count convention:{" "}
                      <span className="font-semibold">
                        {loan.day_count_convention || "ACT_365"}
                      </span>
                      {/* Phase F (2026-10-10) — admin can flip the convention. */}
                      <button
                        type="button"
                        onClick={() => setDayCountOpen(true)}
                        className="ml-2 text-xs text-sky-700 hover:underline"
                        data-testid="edit-day-count-btn"
                      >
                        change…
                      </button>
                    </div>
                  </div>
                  <Button
                    onClick={() => setRebuildOpen(true)}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="rebuild-accruals-btn"
                  >
                    <AlertTriangle className="h-4 w-4 mr-1.5" />
                    Rebuild accruals using IAS 39
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="schedule" className="mt-4">
          <Card><CardContent className="p-0">
            {scheduleLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" /></div>
            ) : (scheduleData.schedule || []).length === 0 ? (
              <div className="text-center py-16 text-slate-500" data-testid="schedule-empty-state">
                <Banknote className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                <p className="font-medium text-slate-700">No disbursements posted yet.</p>
                <p className="text-sm mt-1">Draw funds from the Disbursements tab to see the payment schedule.</p>
              </div>
            ) : (
            <Table data-testid="schedule-table">
              <TableHeader><TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>#</TableHead><TableHead>Due date</TableHead>
                <TableHead className="text-right">Opening bal.</TableHead>
                <TableHead className="text-right">Principal</TableHead>
                <TableHead className="text-right">Interest</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(scheduleData.schedule || []).map((s) => {
                  const tranches = breakdownByDate[s.due_date] || [];
                  const canExpand = tranches.length > 0;
                  const isOpen = expandedRow === s.installment_no;
                  return (
                    <Fragment key={s.installment_no}>
                      <TableRow data-testid={`schedule-row-${s.installment_no}`}>
                        <TableCell className="p-1">
                          {canExpand ? (
                            <Button
                              size="icon" variant="ghost" className="h-6 w-6"
                              onClick={() => setExpandedRow(isOpen ? null : s.installment_no)}
                              data-testid={`schedule-expand-${s.installment_no}`}
                            >
                              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </Button>
                          ) : null}
                        </TableCell>
                        <TableCell>{s.installment_no}</TableCell>
                        <TableCell>
                          {s.due_date}
                          {s.catch_up_interest > 0 && (
                            <Badge className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px]" data-testid={`catch-up-badge-${s.installment_no}`}>
                              catch-up
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(s.opening_balance)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(s.principal_due)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(s.interest_due)}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{fmt(s.total_due)}</TableCell>
                        <TableCell>
                          {s.status === "paid"
                            ? <Badge className="bg-emerald-100 text-emerald-700">paid</Badge>
                            : s.status === "accrued"
                              ? <Badge className="bg-amber-100 text-amber-700" data-testid={`accrued-badge-${s.installment_no}`}>accrued</Badge>
                              : <Badge className="bg-slate-100 text-slate-600">{s.status}</Badge>}
                        </TableCell>
                        <TableCell>
                          {canWrite && isActive && (s.status === "scheduled" || s.status === "accrued") && (
                            <Button size="sm" variant="outline" onClick={() => setPayInst(s)} data-testid={`pay-${s.installment_no}`}>
                              Record Payment
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow className="bg-slate-50" data-testid={`schedule-breakdown-${s.installment_no}`}>
                          <TableCell colSpan={9} className="p-0">
                            <div className="px-4 py-3 border-l-2 border-[#4FA3D1] ml-4">
                              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Per-tranche breakdown</div>
                              <Table>
                                <TableHeader><TableRow>
                                  <TableHead className="text-xs">Disbursement</TableHead>
                                  <TableHead className="text-xs">Drawn on</TableHead>
                                  <TableHead className="text-xs text-right">Tranche</TableHead>
                                  <TableHead className="text-xs text-right">Principal</TableHead>
                                  <TableHead className="text-xs text-right">Interest</TableHead>
                                  <TableHead className="text-xs text-right">Total</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                  {tranches.map((t, ix) => (
                                    <TableRow key={ix} data-testid={`breakdown-tranche-${s.installment_no}-${ix}`}>
                                      <TableCell className="text-xs font-mono">{(t.disbursement_id || "").slice(0, 8)}…</TableCell>
                                      <TableCell className="text-xs">{t.disbursement_date}</TableCell>
                                      <TableCell className="text-xs text-right tabular-nums">{fmt(t.tranche_amount)}</TableCell>
                                      <TableCell className="text-xs text-right tabular-nums">{fmt(t.principal_due)}</TableCell>
                                      <TableCell className="text-xs text-right tabular-nums">
                                        {fmt(t.interest_due)}
                                        {t.catch_up_interest > 0 && (
                                          <span className="ml-1 text-[10px] text-amber-700">(incl. {fmt(t.catch_up_interest)} catch-up)</span>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-xs text-right tabular-nums font-medium">{fmt(t.total_due)}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Inst #</TableHead><TableHead>Paid date</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>JE</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(scheduleData.schedule || []).filter((s) => s.status === "paid").map((s) => (
                  <TableRow key={s.installment_no}>
                    <TableCell>{s.installment_no}</TableCell>
                    <TableCell>{s.paid_date}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(s.paid_amount)}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{(s.je_id || "").slice(0, 8)}…</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="documents" className="mt-4">
          <Card><CardContent className="p-6 text-center text-slate-500" data-testid="documents-tab-empty">
            <FileBarChart2 className="h-7 w-7 mx-auto mb-2 text-slate-300" />
            <p className="text-sm font-medium text-slate-700">No documents attached to this loan yet.</p>
            <p className="text-xs mt-1">Loan agreements, security documents, and statements can be linked from the Documents module (next iteration).</p>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <RecordTrail entity_type="loan" entity_id={loan.id} record={loan} />
        </TabsContent>
      </Tabs>

      {payInst && <PaymentDialog inst={payInst} loanBankCode={loan.bank_account_code}
                                 onCancel={() => setPayInst(null)}
                                 onSubmit={(payload) => recordPayment(payInst, payload)} />}
      {editDisb && (
        <EditDisbursementDateDialog
          loan={loan}
          onCancel={() => setEditDisb(false)}
          onSuccess={() => { setEditDisb(false); onRefresh(); }}
        />
      )}
      {rebuildOpen && (
        <RebuildAccrualsDialog
          loan={loan}
          onCancel={() => setRebuildOpen(false)}
          onSuccess={() => { setRebuildOpen(false); onRefresh(); }}
        />
      )}
      {/* Phase F (2026-10-10) — Day-count convention edit modal. */}
      {dayCountOpen && (
        <EditDayCountDialog
          loan={loan}
          onCancel={() => setDayCountOpen(false)}
          onSuccess={() => { setDayCountOpen(false); onRefresh(); }}
        />
      )}
    </div>
  );
}

function KPI({ label, value, sub, accent = "text-slate-900" }) {
  return (
    <Card><CardContent className="p-4">
      <p className="text-xs uppercase text-slate-500">{label}</p>
      <p className={`text-2xl font-bold tabular-nums ${accent}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </CardContent></Card>
  );
}

function PaymentDialog({ inst, loanBankCode, onCancel, onSubmit }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [bank, setBank] = useState(loanBankCode || "1010");
  const [amount, setAmount] = useState(String(Number(inst.total_due || inst.interest_due || 0)));
  const [memo, setMemo] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!amount || Number(amount) <= 0) return;
    setBusy(true);
    try {
      await onSubmit({
        amount: Number(amount),
        payment_date: date,
        bank_account_code: bank,
        memo: memo || undefined,
      });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Record installment {inst.installment_no} payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-slate-600">
            Due: <span className="font-semibold">{fmt(inst.total_due)}</span>{" "}
            (Principal {fmt(inst.principal_due)} + Interest {fmt(inst.interest_due)})
          </p>
          <div className="rounded-md bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-800">
            This creates a <strong>draft</strong> journal entry on the Approval page. The payment
            is applied to interest first (Dr 2160) then principal (Dr 2200/2210) /{" "}
            Cr the selected bank account.
          </div>
          <div>
            <Label>Payment amount</Label>
            <Input
              type="number" step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="payment-amount-input"
            />
          </div>
          <div>
            <Label>Payment date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="payment-date-input" />
          </div>
          <div>
            <Label>Bank account code</Label>
            <Input value={bank} onChange={(e) => setBank(e.target.value)} data-testid="payment-bank-input" />
          </div>
          <div>
            <Label>Memo (optional)</Label>
            <Input value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="e.g. September 2026 interest payment" data-testid="payment-memo-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="payment-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Receipt className="h-4 w-4 mr-1" />}
            Create draft JE
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Phase D (2026-10-04) — edit a loan's disbursement date. Blocked when any
// schedule row is already accrued/paid.
function EditDisbursementDateDialog({ loan, onCancel, onSuccess }) {
  const [newDate, setNewDate] = useState(loan.start_date || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setErr("");
    if (!newDate) { setErr("Pick a date."); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/loans/${loan.id}/disbursement-date`, {
        disbursement_date: newDate,
      });
      toast.success(
        `Schedule regenerated. First due ${data.after?.first_due}, ` +
        `last due ${data.after?.last_due}.`,
      );
      onSuccess();
    } catch (e) {
      setErr(formatApiError(e) || "Could not edit disbursement date.");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="edit-disbursement-date-dialog">
        <DialogHeader>
          <DialogTitle>Edit disbursement date</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Changing the disbursement date regenerates the amortization schedule
            from scratch. Rows already <strong>accrued</strong> or
            <strong> paid</strong> block the edit — reverse them first via
            Journal Entry → Correct entry.
          </div>
          <div>
            <Label>Current disbursement date</Label>
            <Input value={loan.start_date || "—"} disabled />
          </div>
          <div>
            <Label>New disbursement date</Label>
            <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid="edit-disbursement-new-date" />
          </div>
          {err && <div className="rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-xs" data-testid="edit-disbursement-error">{err}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="edit-disbursement-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CalendarRange className="h-4 w-4 mr-1" />}
            Regenerate schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Phase E (2026-10-04) — IAS 39 rebuild action with MFA step-up.
// Phase E hot-fix (2026-10-10) — added recovery-code fallback.
// Phase E hot-fix round 2 (2026-10-10) — added "Accrue up to date".
function RebuildAccrualsDialog({ loan, onCancel, onSuccess }) {
  const [preview, setPreview] = useState(null);
  const todayIso = new Date().toISOString().slice(0, 10);
  const [asOfDate, setAsOfDate] = useState(todayIso);
  const [otp, setOtp] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    setPreview(null);
    const q = asOfDate ? `?as_of_date=${encodeURIComponent(asOfDate)}` : "";
    api.get(`/loans/${loan.id}/rebuild-preview${q}`)
      .then(({ data }) => setPreview(data))
      .catch((e) => setErr(formatApiError(e) || "Could not load preview."));
  }, [loan.id, asOfDate]);
  const submit = async () => {
    setErr("");
    if (useRecovery) {
      if (!recoveryCode || recoveryCode.trim().length < 6) {
        setErr("Enter a recovery code."); return;
      }
    } else {
      if (!otp || otp.length < 6) { setErr("Enter your 6-digit MFA code."); return; }
    }
    setBusy(true);
    try {
      // 1. Mint a step-up token (TOTP OR recovery code).
      let step;
      if (useRecovery) {
        const { data } = await api.post("/auth/step-up/mfa-recovery", {
          code: recoveryCode.trim().toUpperCase(),
          action: "loan_rebuild_accruals",
        });
        step = data;
      } else {
        const { data } = await api.post("/auth/step-up/mfa", {
          otp, action: "loan_rebuild_accruals",
        });
        step = data;
      }
      // 2. Fire the rebuild.
      const { data } = await api.post(`/loans/${loan.id}/rebuild-accruals`, {
        step_up_token: step.step_up_token,
        as_of_date: asOfDate || undefined,
      });
      toast.success(
        `Loan rebuilt (as of ${asOfDate}) — voided $${Number(data.voided_total).toFixed(2)} ` +
        `(${data.voided_count} JEs, ledger-visible $${Number(data.voided_total_ledger || 0).toFixed(2)}), ` +
        `posted $${Number(data.new_accrual_total).toFixed(2)} ` +
        `(${data.new_accrual_count} JEs). Net TB delta: $${Number(data.net_delta).toFixed(2)}.`
      );
      onSuccess();
    } catch (e) {
      setErr(formatApiError(e) || "Rebuild failed.");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="rebuild-accruals-dialog">
        <DialogHeader>
          <DialogTitle>Rebuild accruals using IAS 39</DialogTitle>
          <DialogDescription className="text-xs">
            Soft-voids historical calendar-math accruals and posts fresh effective-interest
            accruals from the disbursement date. Audit log preserves every change.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            <strong>High-impact action.</strong> Changes will be visible on
            Trial Balance, Interest Expense drilldowns and the Loan ledger.
            Voided JEs stay in the audit log so auditors can trace the change.
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-600 whitespace-nowrap">Accrue up to date:</Label>
            <Input
              type="date" value={asOfDate}
              max="2030-12-31"
              onChange={(e) => setAsOfDate(e.target.value || todayIso)}
              className="h-8 text-xs" data-testid="rebuild-as-of-date"
            />
            <button
              type="button" className="text-xs text-sky-700 hover:underline"
              onClick={() => setAsOfDate(todayIso)} data-testid="rebuild-as-of-today"
              title="Reset to today"
            >
              Today
            </button>
          </div>
          {!preview ? (
            <div className="text-xs text-slate-500">Loading preview…</div>
          ) : (
            <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs grid grid-cols-2 gap-1" data-testid="rebuild-preview">
              <div className="text-slate-500">Loan</div>
              <div className="text-right font-mono">{preview.loan_number}</div>
              <div className="text-slate-500">Disbursement</div>
              <div className="text-right font-mono">{preview.disbursement_date}</div>
              <div className="text-slate-500">Day-count</div>
              <div className="text-right font-mono">{preview.day_count}</div>
              <div className="text-slate-500 mt-1">Will VOID</div>
              <div className="text-right font-semibold text-rose-700" data-testid="rebuild-preview-void">
                {preview.voided_count} JE{preview.voided_count === 1 ? "" : "s"}{" "}
                / ${Number(preview.voided_total).toFixed(2)}
              </div>
              <div className="text-slate-500">Will POST (IAS 39)</div>
              <div className="text-right font-semibold text-emerald-700" data-testid="rebuild-preview-new">
                {preview.new_accrual_count} JE{preview.new_accrual_count === 1 ? "" : "s"}{" "}
                / ${Number(preview.new_accrual_total).toFixed(2)}
              </div>
              <div className="text-slate-500">Net ledger delta</div>
              <div className={"text-right font-semibold " + (preview.net_delta < 0 ? "text-rose-800" : "text-emerald-800")} data-testid="rebuild-preview-delta">
                ${Number(preview.net_delta).toFixed(2)}
              </div>
            </div>
          )}
          <div>
            {useRecovery ? (
              <>
                <Label>Confirm with a recovery code</Label>
                <Input
                  type="text" inputMode="text"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  placeholder="XXXXX-XXXXX"
                  autoComplete="one-time-code"
                  data-testid="rebuild-mfa-recovery"
                />
                <button
                  type="button"
                  className="mt-2 text-xs text-sky-700 hover:underline"
                  onClick={() => { setUseRecovery(false); setRecoveryCode(""); setErr(""); }}
                  data-testid="rebuild-mfa-use-totp"
                >
                  Use authenticator code instead
                </button>
              </>
            ) : (
              <>
                <Label>Confirm with your 6-digit MFA code</Label>
                <Input
                  type="text" inputMode="numeric" maxLength={6}
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  data-testid="rebuild-mfa-otp"
                />
                <button
                  type="button"
                  className="mt-2 text-xs text-sky-700 hover:underline"
                  onClick={() => { setUseRecovery(true); setOtp(""); setErr(""); }}
                  data-testid="rebuild-mfa-use-recovery"
                >
                  Use recovery code instead
                </button>
              </>
            )}
          </div>
          {err && <div className="rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-xs" data-testid="rebuild-error">{err}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || (useRecovery ? !recoveryCode : !otp)}
            className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="rebuild-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
            Rebuild
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Phase F (2026-10-10) — day-count convention editor with MFA step-up.
// Flipping convention does NOT re-post interest; the toast on success
// nudges the admin to run Rebuild Accruals from the same card.
function EditDayCountDialog({ loan, onCancel, onSuccess }) {
  const current = loan.day_count_convention || "ACT_365";
  const [convention, setConvention] = useState(current);
  const [reason, setReason] = useState("");
  const [otp, setOtp] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const CONVENTIONS = [
    { v: "ACT_365", label: "Actual / 365 (IAS 39 simple)", note: "Default. Simple interest: P × r × days ÷ 365." },
    { v: "COMPOUND_ACT_365", label: "Compound Actual / 365 (IAS 39 EIR)", note: "Auditor's IAS 39 effective-interest convention. Daily compounding: P × ((1 + r)^(days ÷ 365) − 1). Matches Deloitte's audit template." },
    { v: "_30_360", label: "30 / 360 (Turkish / corporate convention)", note: "Both month-end days clipped to 30; year = 360." },
    { v: "_30_360_US", label: "30 / 360 US (NASD / Bond Basis)", note: "US variant; D2=31 only clipped when D1 is already 30." },
    { v: "ACT_360", label: "Actual / 360", note: "Actual days ÷ 360 (money-market convention)." },
  ];
  const submit = async () => {
    setErr("");
    if (convention === current) {
      setErr("Pick a different convention or Cancel.");
      return;
    }
    if (useRecovery) {
      if (!recoveryCode || recoveryCode.trim().length < 6) {
        setErr("Enter a recovery code."); return;
      }
    } else {
      if (!otp || otp.length < 6) {
        setErr("Enter your 6-digit MFA code."); return;
      }
    }
    setBusy(true);
    try {
      let step;
      if (useRecovery) {
        const { data } = await api.post("/auth/step-up/mfa-recovery", {
          code: recoveryCode.trim().toUpperCase(),
          action: "loan_day_count_convention_change",
        });
        step = data;
      } else {
        const { data } = await api.post("/auth/step-up/mfa", {
          otp, action: "loan_day_count_convention_change",
        });
        step = data;
      }
      const { data } = await api.patch(
        `/loans/${loan.id}/day-count-convention`,
        {
          step_up_token: step.step_up_token,
          day_count_convention: convention,
          reason: reason.trim() || undefined,
        },
      );
      toast.success(
        `Convention changed: ${current} → ${data.day_count_convention}. ` +
        "Run Rebuild Accruals to re-post interest under the new convention.",
        { duration: 10000 },
      );
      onSuccess();
    } catch (e) {
      setErr(formatApiError(e) || "Convention change failed.");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="edit-day-count-dialog">
        <DialogHeader>
          <DialogTitle>Change day-count convention</DialogTitle>
          <DialogDescription className="text-xs">
            Flipping the convention changes how interest days are counted
            on the next Rebuild. It does NOT re-post anything on its own —
            you'll need to run Rebuild Accruals afterwards.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
            <div className="text-slate-500">Loan</div>
            <div className="font-mono text-slate-800">{loan.loan_number} · {loan.lender_name}</div>
            <div className="text-slate-500 mt-1">Current convention</div>
            <div className="font-mono text-slate-800">{current}</div>
          </div>
          <div>
            <Label className="text-xs text-slate-600">New convention</Label>
            <select
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
              value={convention}
              onChange={(e) => setConvention(e.target.value)}
              data-testid="day-count-select"
            >
              {CONVENTIONS.map((c) => (
                <option key={c.v} value={c.v}>{c.label}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-slate-500" data-testid="day-count-note">
              {CONVENTIONS.find((c) => c.v === convention)?.note}
            </p>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Reason (optional)</Label>
            <Input
              type="text" value={reason} maxLength={512}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Auditor reconciliation request #124"
              data-testid="day-count-reason"
            />
          </div>
          <div>
            {useRecovery ? (
              <>
                <Label>Confirm with a recovery code</Label>
                <Input
                  type="text" value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  placeholder="XXXXX-XXXXX"
                  data-testid="day-count-mfa-recovery"
                />
                <button
                  type="button"
                  className="mt-2 text-xs text-sky-700 hover:underline"
                  onClick={() => { setUseRecovery(false); setRecoveryCode(""); setErr(""); }}
                  data-testid="day-count-mfa-use-totp"
                >
                  Use authenticator code instead
                </button>
              </>
            ) : (
              <>
                <Label>Confirm with your 6-digit MFA code</Label>
                <Input
                  type="text" inputMode="numeric" maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  data-testid="day-count-mfa-otp"
                />
                <button
                  type="button"
                  className="mt-2 text-xs text-sky-700 hover:underline"
                  onClick={() => { setUseRecovery(true); setOtp(""); setErr(""); }}
                  data-testid="day-count-mfa-use-recovery"
                >
                  Use recovery code instead
                </button>
              </>
            )}
          </div>
          {err && (
            <div className="rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-xs" data-testid="day-count-error">
              {err}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || convention === current || (useRecovery ? !recoveryCode : !otp)}
            className="bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="day-count-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Change convention
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Phase E hot-fix (2026-10-10) — portfolio-wide IAS 39 rebuild with MFA
// step-up. Supports optional loan_number_prefix filter and a recovery-
// code fallback mirroring the per-loan dialog.
// Round 2 (2026-10-10) — added "Accrue up to date" picker so admins
// can cap at a period-close date (e.g. Q3 2026-09-30).
function RebuildAllAccrualsDialog({ onCancel, onSuccess }) {
  const [preview, setPreview] = useState(null);
  const [scope, setScope] = useState("all"); // "all" | "oyak"
  const todayIso = new Date().toISOString().slice(0, 10);
  // Phase F.3b (2026-10-11) — default to the most recent calendar
  // quarter-end instead of today. Period-close rebuilds are the normal
  // use case; a mid-quarter rebuild is an exception. Admins can still
  // click Today to switch back.
  const mostRecentQuarterEnd = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = d.getMonth();  // 0..11
    // Quarter ends: Mar 31, Jun 30, Sep 30, Dec 31.
    const endsByQ = ["03-31", "06-30", "09-30", "12-31"];
    // Which quarter is the current month in? (q = 0..3)
    const q = Math.floor(m / 3);
    // If today is on or past the end of the current quarter, that
    // end is still "most recent"; otherwise step back one quarter.
    const currentQEnd = new Date(`${y}-${endsByQ[q]}`);
    if (d >= currentQEnd) return `${y}-${endsByQ[q]}`;
    if (q === 0) return `${y - 1}-${endsByQ[3]}`;
    return `${y}-${endsByQ[q - 1]}`;
  })();
  const [asOfDate, setAsOfDate] = useState(mostRecentQuarterEnd);
  const [otp, setOtp] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const prefix = scope === "oyak" ? "OYAK-" : null;
  useEffect(() => {
    setPreview(null);
    const q = new URLSearchParams();
    if (prefix) q.set("loan_number_prefix", prefix);
    if (asOfDate) q.set("as_of_date", asOfDate);
    const qs = q.toString() ? `?${q.toString()}` : "";
    api.get(`/loans/rebuild-accruals/preview-all${qs}`)
      .then(({ data }) => setPreview(data))
      .catch((e) => setErr(formatApiError(e) || "Could not load preview."));
  }, [scope, prefix, asOfDate]);
  const submit = async () => {
    setErr("");
    if (useRecovery) {
      if (!recoveryCode || recoveryCode.trim().length < 6) {
        setErr("Enter a recovery code."); return;
      }
    } else {
      if (!otp || otp.length < 6) { setErr("Enter your 6-digit MFA code."); return; }
    }
    setBusy(true);
    try {
      let step;
      if (useRecovery) {
        const { data } = await api.post("/auth/step-up/mfa-recovery", {
          code: recoveryCode.trim().toUpperCase(),
          action: "loan_rebuild_accruals_bulk",
        });
        step = data;
      } else {
        const { data } = await api.post("/auth/step-up/mfa", {
          otp, action: "loan_rebuild_accruals_bulk",
        });
        step = data;
      }
      const { data } = await api.post("/loans/rebuild-accruals/all", {
        step_up_token: step.step_up_token,
        loan_number_prefix: prefix || undefined,
        as_of_date: asOfDate || undefined,
      });
      const t = data.totals || {};
      toast.success(
        `${data.loans_rebuilt}/${data.loans_scanned} loans rebuilt (as of ${asOfDate}). ` +
        `Voided $${Number(t.voided_total_ledger || 0).toFixed(2)} ledger-visible, ` +
        `posted $${Number(t.new_accrual_total || 0).toFixed(2)}, ` +
        `net TB delta $${Number(t.net_delta || 0).toFixed(2)}.`,
        { duration: 10000 },
      );
      onSuccess();
    } catch (e) {
      setErr(formatApiError(e) || "Bulk rebuild failed.");
    } finally { setBusy(false); }
  };
  const totals = preview?.totals || {};
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-xl" data-testid="rebuild-all-accruals-dialog">
        <DialogHeader>
          <DialogTitle>Rebuild ALL loan accruals (IAS 39)</DialogTitle>
          <DialogDescription className="text-xs">
            Portfolio-wide reconciliation. Soft-voids every historical auto-accrual (and
            legacy seed JE) whose amount doesn't match the IAS 39 Actual/365 target and
            posts fresh ones. Idempotent and reversible via audit log.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            <strong>High-impact action.</strong> Trial Balance account 2160 Interest
            Payable will move by the net ledger delta shown below.
          </div>
          <div className="flex items-center gap-3 text-xs">
            <Label className="text-slate-600">Scope:</Label>
            <label className="flex items-center gap-1">
              <input
                type="radio" name="bulk-scope" value="all"
                checked={scope === "all"}
                onChange={() => setScope("all")}
                data-testid="rebuild-all-scope-all"
              />
              <span>Whole book (all active loans)</span>
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio" name="bulk-scope" value="oyak"
                checked={scope === "oyak"}
                onChange={() => setScope("oyak")}
                data-testid="rebuild-all-scope-oyak"
              />
              <span>OYAK loans only</span>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-600 whitespace-nowrap">Accrue up to date:</Label>
            <Input
              type="date" value={asOfDate}
              max="2030-12-31"
              onChange={(e) => setAsOfDate(e.target.value || mostRecentQuarterEnd)}
              className="h-8 text-xs w-40" data-testid="rebuild-all-as-of-date"
            />
            <button
              type="button" className="text-xs text-sky-700 hover:underline"
              onClick={() => setAsOfDate(mostRecentQuarterEnd)}
              data-testid="rebuild-all-as-of-quarter-end"
              title={`Reset to most recent quarter-end (${mostRecentQuarterEnd})`}
            >
              Quarter-end
            </button>
            <button
              type="button" className="text-xs text-sky-700 hover:underline"
              onClick={() => setAsOfDate(todayIso)} data-testid="rebuild-all-as-of-today"
              title="Set to today"
            >
              Today
            </button>
          </div>
          <p className="text-[11px] text-slate-500 -mt-1 ml-[7.75rem]" data-testid="rebuild-all-as-of-help">
            Default = most recent quarter-end ({mostRecentQuarterEnd}). Change if you need a different reporting cutoff.
          </p>
          {!preview ? (
            <div className="text-xs text-slate-500">Loading preview…</div>
          ) : (
            <div className="rounded border border-slate-200 bg-white text-xs" data-testid="rebuild-all-preview">
              <div className="grid grid-cols-7 bg-slate-50 border-b font-semibold text-slate-600 px-2 py-1">
                <div>Loan</div>
                <div className="text-center">Conv</div>
                <div className="text-right">Void count</div>
                <div className="text-right">Void (ledger)</div>
                <div className="text-right">New count</div>
                <div className="text-right">New total</div>
                <div className="text-right">Δ (TB)</div>
              </div>
              {(preview.loans || []).map((r) => (
                <div key={r.loan_number} className="grid grid-cols-7 px-2 py-1 border-b border-slate-100 last:border-0">
                  <div className="font-mono text-slate-700">{r.loan_number}</div>
                  <div className="text-center">
                    <span
                      className={"inline-block text-[10px] py-0 px-1.5 rounded border font-medium " + (
                        (r.day_count || "ACT_365") === "COMPOUND_ACT_365" ? "border-emerald-400 bg-emerald-50 text-emerald-800" :
                        (r.day_count || "ACT_365") === "_30_360" ? "border-amber-300 bg-amber-50 text-amber-800" :
                        (r.day_count || "ACT_365") === "_30_360_US" ? "border-orange-300 bg-orange-50 text-orange-800" :
                        (r.day_count || "ACT_365") === "ACT_360" ? "border-slate-300 bg-slate-50 text-slate-700" :
                        "border-sky-300 bg-sky-50 text-sky-800")}
                      data-testid={`rebuild-all-conv-${r.loan_number}`}
                    >
                      {(r.day_count || "ACT_365") === "COMPOUND_ACT_365" ? "EIR" :
                       (r.day_count || "ACT_365") === "_30_360" ? "30/360" :
                       (r.day_count || "ACT_365") === "_30_360_US" ? "30/360-US" :
                       (r.day_count || "ACT_365") === "ACT_360" ? "A/360" :
                       "A/365"}
                    </span>
                  </div>
                  <div className="text-right">{r.voided_count ?? "—"}</div>
                  <div className="text-right">${Number(r.voided_total_ledger || 0).toFixed(2)}</div>
                  <div className="text-right">{r.new_accrual_count ?? "—"}</div>
                  <div className="text-right">${Number(r.new_accrual_total || 0).toFixed(2)}</div>
                  <div className={"text-right " + ((r.net_delta || 0) < 0 ? "text-rose-700" : "text-emerald-700")}>
                    ${Number(r.net_delta || 0).toFixed(2)}
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-7 bg-slate-50 border-t px-2 py-1 font-semibold">
                <div>Totals</div>
                <div />
                <div className="text-right">{totals.voided_count || 0}</div>
                <div className="text-right" data-testid="rebuild-all-preview-void-ledger">
                  ${Number(totals.voided_total_ledger || 0).toFixed(2)}
                </div>
                <div className="text-right">{totals.new_accrual_count || 0}</div>
                <div className="text-right" data-testid="rebuild-all-preview-new-total">
                  ${Number(totals.new_accrual_total || 0).toFixed(2)}
                </div>
                <div className={"text-right " + ((totals.net_delta || 0) < 0 ? "text-rose-700" : "text-emerald-700")} data-testid="rebuild-all-preview-delta">
                  ${Number(totals.net_delta || 0).toFixed(2)}
                </div>
              </div>
            </div>
          )}
          <div>
            {useRecovery ? (
              <>
                <Label>Confirm with a recovery code</Label>
                <Input
                  type="text" inputMode="text"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  placeholder="XXXXX-XXXXX"
                  autoComplete="one-time-code"
                  data-testid="rebuild-all-mfa-recovery"
                />
                <button
                  type="button"
                  className="mt-2 text-xs text-sky-700 hover:underline"
                  onClick={() => { setUseRecovery(false); setRecoveryCode(""); setErr(""); }}
                  data-testid="rebuild-all-mfa-use-totp"
                >
                  Use authenticator code instead
                </button>
              </>
            ) : (
              <>
                <Label>Confirm with your 6-digit MFA code</Label>
                <Input
                  type="text" inputMode="numeric" maxLength={6}
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  data-testid="rebuild-all-mfa-otp"
                />
                <button
                  type="button"
                  className="mt-2 text-xs text-sky-700 hover:underline"
                  onClick={() => { setUseRecovery(true); setOtp(""); setErr(""); }}
                  data-testid="rebuild-all-mfa-use-recovery"
                >
                  Use recovery code instead
                </button>
              </>
            )}
          </div>
          {err && <div className="rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-xs" data-testid="rebuild-all-error">{err}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !preview || (useRecovery ? !recoveryCode : !otp)}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="rebuild-all-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
            Rebuild {scope === "oyak" ? "OYAK loans" : "whole book"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisbursementsTab({ loan, canWrite, onRefresh }) {
  // Phase 27 (2026-07-15) — partial drawdowns against a facility.
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/loans/${loan.id}/disbursements`);
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [loan.id]);
  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Amount must be positive"); return; }
    setBusy(true);
    try {
      await api.post(`/loans/${loan.id}/disbursements`, {
        amount: amt, disbursement_date: date, reference: ref,
        bank_account_code: "1010",
      });
      toast.success(`Disbursed $${amt.toLocaleString()}`);
      setShowNew(false); setAmount(""); setRef("");
      load(); onRefresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const voidOne = async (disb) => {
    if (!window.confirm(`Void disbursement of $${disb.amount.toLocaleString()}? This will reverse the JE.`)) return;
    try {
      await api.delete(`/loans/${loan.id}/disbursements/${disb.id}`);
      toast.success("Disbursement voided");
      load(); onRefresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="disbursements-tab">
      <div className="grid sm:grid-cols-5 gap-3">
        <KPI label="Facility Limit" value={fmt(loan.facility_limit || loan.principal_amount)} />
        <KPI label="Intended Drawdown" value={fmt(loan.intended_drawdown || loan.principal_amount)} accent="text-[#2F86B5]" />
        <KPI label="Total Drawn" value={fmt(loan.total_drawn || 0)} accent="text-emerald-700" />
        <KPI label="Available to Draw" value={fmt(loan.available_to_draw ?? loan.facility_limit ?? 0)} accent="text-amber-700" />
        <KPI label="Outstanding" value={fmt(loan.outstanding_principal || 0)} accent="text-[#E63946]" />
      </div>
      {canWrite && loan.status !== "voided" && (
        <Button onClick={() => setShowNew(true)} className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white" data-testid="new-disbursement-btn">
          <Banknote className="h-4 w-4 mr-1.5" /> New Disbursement
        </Button>
      )}
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Reference</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Status</TableHead><TableHead>JE</TableHead>
            {canWrite && <TableHead></TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="text-center py-6">Loading…</TableCell></TableRow>
             : items.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">No disbursements yet.</TableCell></TableRow>
             : items.map((d) => (
                <TableRow key={d.id} data-testid={`disbursement-row-${d.id}`}>
                  <TableCell>{d.disbursement_date}</TableCell>
                  <TableCell className="text-xs">{d.reference}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{fmt(d.amount)}</TableCell>
                  <TableCell><Badge className={d.status === "voided" ? "bg-slate-200 text-slate-500 line-through" : "bg-emerald-100 text-emerald-700"}>{d.status}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-400">{(d.je_id || "").slice(0, 8)}</TableCell>
                  {canWrite && (
                    <TableCell className="text-right">
                      {d.status === "posted" && (
                        <button onClick={() => voidOne(d)} className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-red-50 text-red-600" title="Void" data-testid={`void-disb-${d.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md" data-testid="disbursement-dialog">
          <DialogHeader><DialogTitle>New disbursement · {loan.loan_number}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-slate-500">Available to draw: <span className="font-semibold">{fmt(loan.available_to_draw ?? loan.facility_limit ?? 0)}</span></p>
            <div><Label className="text-xs">Amount (USD)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="disb-amount" /></div>
            <div><Label className="text-xs">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div><Label className="text-xs">Reference</Label><Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="OYAK-DIS-001" data-testid="disb-ref" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white" data-testid="disb-submit">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Draw down
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateLoanDialog({ open, onOpenChange, onCreated, initial }) {
  // P1 (2026-07-14) — dual-purpose: create-new (initial=undefined) or
  // edit-existing (initial=loanDoc). Editing an ACTIVE loan locks the
  // financial fields; a helper text explains why.
  const isEdit = !!initial;
  const isActive = initial?.status === "active";
  const isVoided = initial?.status === "voided";
  const lockedFinancials = isEdit && isActive;
  // Phase F.3 (2026-10-11) — day-count convention editor inside the Edit
  // Loan dialog. Convention is NOT locked by the "financial fields are
  // locked" banner (auditor-driven change gated by MFA step-up instead).
  const [convOpen, setConvOpen] = useState(false);
  const [currentConv, setCurrentConv] = useState(
    initial?.day_count_convention || "ACT_365");
  const CONV_LABEL = {
    ACT_365: "Actual / 365 (IAS 39 simple)",
    COMPOUND_ACT_365: "Compound Actual / 365 (IAS 39 EIR)",
    _30_360: "30 / 360 (Turkish / corporate)",
    _30_360_US: "30 / 360 US (NASD)",
    ACT_360: "Actual / 360",
  };
  // Phase 52-B (2026-08-06) — bank/cash whitelist for the receiving-account
  // dropdown; mirrors the Phase 51-B `_is_bank_or_cash_account` predicate.
  const [bankOptions, setBankOptions] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/accounts").then((r) => {
      const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      const banks = rows.filter((a) => {
        if (a.type !== "asset") return false;
        if (a.is_active === false) return false;
        const code = String(a.code || "").trim();
        if (code === "1000" || code === "1100") return true;
        if (a.parent_code === "1010") return true;
        const n = parseInt(code, 10);
        return !isNaN(n) && n >= 1010 && n <= 1099;
      }).sort((a, b) => String(a.code).localeCompare(String(b.code)));
      setBankOptions(banks);
    }).catch(() => setBankOptions([]));
  }, [open]);
  const [f, setF] = useState(() => (initial ? {
    loan_number: initial.loan_number || "",
    lender_name: initial.lender_name || "",
    lender_type: initial.lender_type || "bank",
    // Phase 52-B (2026-08-06) — loan_type classification distinct from
    // lender_type; drives the Cr-side liability account on disburse.
    loan_type: initial.loan_type || "long_term",
    // Phase 52-B (2026-08-06) — receiving bank captured on the form and
    // reused by the /disburse and /payoff endpoints.
    bank_account_code: initial.bank_account_code || "",
    principal_amount: initial.principal_amount ?? "",
    interest_rate: initial.interest_rate ?? "",
    interest_type: initial.interest_type || "reducing_balance",
    start_date: initial.start_date || new Date().toISOString().slice(0, 10),
    term_months: initial.term_months ?? 12,
    payment_frequency: initial.payment_frequency || "monthly",
    collateral: initial.collateral || "",
    notes: initial.notes || "",
    wht_on_interest_rate: initial.wht_on_interest_rate ?? 10.0,
    wht_on_interest_account_code: initial.wht_on_interest_account_code || "2130",
    // Phase 27-b (2026-07-15) — interest accrual anchor. Legacy loans
    // may still carry the historical alias `on_drawn_balance`; normalise
    // to the SOMTURK/OYAK default.
    interest_accrual_mode:
      (initial.interest_accrual_mode === "on_drawn_balance"
        ? "from_loan_start"
        : initial.interest_accrual_mode) || "from_loan_start",
    // Phase 27-c (2026-07-16) — accounting recording date (defaults to today
    // on new loans; falls back to the loan's stored value on edit).
    recording_date:
      initial.recording_date ||
      (initial.created_at ? String(initial.created_at).slice(0, 10) : new Date().toISOString().slice(0, 10)),
    is_pre_existing: !!initial.is_pre_existing,
    opening_principal_balance: initial.opening_principal_balance ?? "",
    opening_interest_accrued: initial.opening_interest_accrued ?? 0,
    historical_payments_count: initial.historical_payments_count ?? 0,
  } : {
    loan_number: "", lender_name: "", lender_type: "bank",
    // Phase 52-B (2026-08-06) — new loans default to long_term. Users
    // pick short_term for < 12-month working capital facilities.
    loan_type: "long_term",
    bank_account_code: "",
    principal_amount: "", interest_rate: "", interest_type: "reducing_balance",
    start_date: new Date().toISOString().slice(0, 10),
    term_months: 12, payment_frequency: "monthly", collateral: "", notes: "",
    // P1 (2026-07-14) — Somalia WHT on interest + pre-existing loan support
    wht_on_interest_rate: 10.0,
    wht_on_interest_account_code: "2130",
    // Phase 27-b (2026-07-15) — SOMTURK/OYAK contract default.
    interest_accrual_mode: "from_loan_start",
    // Phase 27-c (2026-07-16) — accounting recording date defaults to today.
    recording_date: new Date().toISOString().slice(0, 10),
    is_pre_existing: false,
    opening_principal_balance: "",
    opening_interest_accrued: 0,
    historical_payments_count: 0,
  }));
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // Live "Loan Summary" preview
  const p = Number(f.principal_amount) || 0;
  const r = Number(f.interest_rate) || 0;
  const wht = Number(f.wht_on_interest_rate) || 0;
  const openPrin = f.is_pre_existing && f.opening_principal_balance !== ""
    ? Number(f.opening_principal_balance) : p;
  const monthly_i = (openPrin * r / 100) / 12;
  const monthly_wht = monthly_i * wht / 100;
  const monthly_net_i = monthly_i - monthly_wht;
  const fmtUSD = (v) => (v < 0 ? `($${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

  const submit = async () => {
    try {
      const payload = {
        ...f,
        principal_amount: Number(f.principal_amount),
        interest_rate: Number(f.interest_rate),
        term_months: Number(f.term_months),
        wht_on_interest_rate: Number(f.wht_on_interest_rate),
        opening_interest_accrued: Number(f.opening_interest_accrued) || 0,
        historical_payments_count: Number(f.historical_payments_count) || 0,
      };
      // Phase 52-B (2026-08-06) — drop empty bank_account_code so the
      // backend keeps it as null on the loan record (Disburse then falls
      // back to the payload / 1010 default). Prevents the value from
      // being stored as literally empty string in Mongo.
      if (!payload.bank_account_code) delete payload.bank_account_code;
      if (f.is_pre_existing && f.opening_principal_balance !== "") {
        payload.opening_principal_balance = Number(f.opening_principal_balance);
      } else {
        delete payload.opening_principal_balance;
      }
      if (isEdit) {
        // Drop the locked financial fields when patching an active loan
        // so the API doesn't 400 on an untouched field.
        if (lockedFinancials) {
          ["principal_amount", "interest_rate", "interest_type",
           "term_months", "start_date", "payment_frequency"].forEach((k) => delete payload[k]);
        }
        delete payload.loan_number;  // never editable on existing
        await api.patch(`/loans/${initial.id}`, payload);
        toast.success("Loan updated");
      } else {
        await api.post("/loans", payload);
        toast.success("Loan created (draft). Use Disburse to post the drawdown JE.");
      }
      onCreated();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-loan-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit loan · ${initial.loan_number}` : "New loan"}</DialogTitle>
        </DialogHeader>
        {lockedFinancials && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="edit-lock-notice">
            This loan is <span className="font-semibold">active</span> — financial fields are locked.
            Void the loan first to change principal, rate, term, start date, interest type, or frequency.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 py-2">
          <Field label="Loan number" v={f.loan_number} on={(v) => set("loan_number", v)} testId="ln-no" disabled={isEdit} />
          <Field label="Lender name" v={f.lender_name} on={(v) => set("lender_name", v)} testId="ln-lender" />
          <SelField label="Lender type" v={f.lender_type} on={(v) => set("lender_type", v)} options={["bank", "individual", "government", "shareholder", "other"]} />
          <div>
            <Label className="text-xs">Loan type</Label>
            <Select value={f.loan_type} onValueChange={(v) => set("loan_type", v)}>
              <SelectTrigger className="mt-1" data-testid="ln-loan-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="short_term" data-testid="loan-type-short-term">
                  Short-term (credits 2210)
                </SelectItem>
                <SelectItem value="long_term" data-testid="loan-type-long-term">
                  Long-term (credits 2200)
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-0.5">Drives the liability GL account on the disbursement JE.</p>
          </div>
          <div>
            <Label className="text-xs">Receiving bank account</Label>
            <Select value={f.bank_account_code || ""} onValueChange={(v) => set("bank_account_code", v)}>
              <SelectTrigger className="mt-1" data-testid="ln-bank-account"><SelectValue placeholder="Pick a bank/cash account…" /></SelectTrigger>
              <SelectContent>
                {bankOptions.length === 0 ? (
                  <SelectItem value="__none__" disabled data-testid="ln-bank-none">
                    Loading bank accounts…
                  </SelectItem>
                ) : bankOptions.map((a) => (
                  <SelectItem key={a.code} value={String(a.code)} data-testid={`ln-bank-opt-${a.code}`}>
                    {a.code} · {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-0.5">Where the funds are received on disbursement. Reused on payoff.</p>
          </div>
          <Field label="Principal (USD)" v={f.principal_amount} on={(v) => set("principal_amount", v)} type="number" testId="ln-principal" disabled={lockedFinancials} />
          <Field label="Annual rate %" v={f.interest_rate} on={(v) => set("interest_rate", v)} type="number" testId="ln-rate" disabled={lockedFinancials} />
          <SelField label="Interest type" v={f.interest_type} on={(v) => set("interest_type", v)} options={["reducing_balance", "flat"]} disabled={lockedFinancials} />
          <div>
            <Label className="text-xs">Interest accrual anchor</Label>
            <Select value={f.interest_accrual_mode} onValueChange={(v) => set("interest_accrual_mode", v)}>
              <SelectTrigger className="mt-1" data-testid="ln-accrual-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="from_loan_start" data-testid="accrual-from-loan-start">
                  Interest from loan start date (SOMTURK/OYAK contract default)
                </SelectItem>
                <SelectItem value="from_disbursement" data-testid="accrual-from-disbursement">
                  Interest from actual disbursement date (standard)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Field label="Start date" v={f.start_date} on={(v) => set("start_date", v)} type="date" disabled={lockedFinancials} />
          <div>
            <Label className="text-xs">Recording date</Label>
            <Input type="date" value={f.recording_date}
                   onChange={(e) => set("recording_date", e.target.value)}
                   className="mt-1"
                   data-testid="ln-recording-date" />
            <p className="text-[10px] text-slate-500 mt-0.5">When this loan is being recorded in the books.</p>
          </div>
          <Field label="Term (months)" v={f.term_months} on={(v) => set("term_months", v)} type="number" disabled={lockedFinancials} />
          <SelField label="Frequency" v={f.payment_frequency} on={(v) => set("payment_frequency", v)} options={[
            {value: "monthly", label: "monthly"},
            {value: "quarterly", label: "quarterly"},
            {value: "annual", label: "annual"},
            {value: "at_maturity", label: "Bullet (pay at maturity)"},
          ]} disabled={lockedFinancials} />
          {(() => {
            const _start = f.start_date;
            const _months = Number(f.term_months) || 0;
            let _end = "";
            if (_start && _months > 0) {
              const [_y, _m, _d] = _start.split("-").map((x) => parseInt(x, 10));
              const _endDate = new Date(Date.UTC(_y, _m - 1 + _months, _d));
              _end = _endDate.toISOString().slice(0, 10);
            }
            return _start && _end ? (
              <div className="col-span-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-sm" data-testid="ln-loan-year-display">
                <span className="text-slate-500 mr-2">Loan Year:</span>
                <span className="font-semibold">{fmtLoanDate(_start)}</span>
                <span className="mx-2 text-slate-400">→</span>
                <span className="font-semibold">{fmtLoanDate(_end)}</span>
              </div>
            ) : null;
          })()}
          <Field label="Collateral" v={f.collateral} on={(v) => set("collateral", v)} />

          {/* Phase F.3 (2026-10-11) — Day-count convention editor.
              Convention is NOT locked by the active-loan banner; auditor
              changes are gated by MFA step-up instead. Edit-mode only. */}
          {isEdit && (
            <div className="col-span-2 rounded-md border border-sky-200 bg-sky-50/50 px-3 py-3 mt-1" data-testid="edit-conv-row">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <Label className="text-xs">Day-count convention</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <span
                      className={"inline-block text-[11px] py-0.5 px-2 rounded border font-medium " + (
                        currentConv === "COMPOUND_ACT_365" ? "border-emerald-400 bg-emerald-50 text-emerald-800" :
                        currentConv === "_30_360" ? "border-amber-300 bg-amber-50 text-amber-800" :
                        currentConv === "_30_360_US" ? "border-orange-300 bg-orange-50 text-orange-800" :
                        currentConv === "ACT_360" ? "border-slate-300 bg-slate-50 text-slate-700" :
                        "border-sky-300 bg-sky-50 text-sky-800")}
                      data-testid="edit-conv-current-pill"
                    >
                      {currentConv === "COMPOUND_ACT_365" ? "EIR" :
                       currentConv === "_30_360" ? "30/360" :
                       currentConv === "_30_360_US" ? "30/360-US" :
                       currentConv === "ACT_360" ? "A/360" : "A/365"}
                    </span>
                    <span className="text-sm text-slate-800 font-medium truncate" data-testid="edit-conv-current-label">
                      {CONV_LABEL[currentConv] || currentConv}
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConvOpen(true)}
                  data-testid="edit-conv-change-btn"
                  className="shrink-0 border-sky-400 text-sky-700 hover:bg-sky-100"
                >
                  Change…
                </Button>
              </div>
              <p className="text-[11px] text-slate-600 mt-2 leading-relaxed" data-testid="edit-conv-help">
                Changing the convention requires MFA step-up. Changes do <span className="font-semibold">not</span> re-post interest
                automatically — run <span className="font-semibold">Rebuild accruals (IAS&nbsp;39)</span> from the loan card afterward.
                Changing the convention is <span className="font-semibold">always allowed</span> via MFA step-up, even on active loans.
              </p>
            </div>
          )}

          {/* WHT on interest (Somalia default 10%) */}
          <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
            <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Withholding tax on interest (Somalia)</Label>
            <div className="grid grid-cols-2 gap-3">
              <Field label="WHT on interest %" v={f.wht_on_interest_rate} on={(v) => set("wht_on_interest_rate", v)} type="number" testId="ln-wht-rate" />
              <Field label="WHT Payable GL code" v={f.wht_on_interest_account_code} on={(v) => set("wht_on_interest_account_code", v)} testId="ln-wht-code" />
            </div>
          </div>

          {/* Pre-existing / mid-term loan */}
          <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={f.is_pre_existing}
                     onChange={(e) => set("is_pre_existing", e.target.checked)}
                     data-testid="ln-preexisting" className="h-4 w-4" />
              <span className="font-medium">This loan started before the system was implemented</span>
            </label>
            {f.is_pre_existing && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Opening principal balance (USD)" v={f.opening_principal_balance} on={(v) => set("opening_principal_balance", v)} type="number" testId="ln-open-prin" />
                <Field label="Opening interest accrued (USD)" v={f.opening_interest_accrued} on={(v) => set("opening_interest_accrued", v)} type="number" />
                <Field label="Historical payments made" v={f.historical_payments_count} on={(v) => set("historical_payments_count", v)} type="number" testId="ln-hist-count" />
              </div>
            )}
          </div>

          {/* Live loan summary preview */}
          {p > 0 && r > 0 && (
            <div className="col-span-2 mt-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="loan-summary-preview">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Loan summary preview</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 tabular-nums">
                <span className="text-slate-600">Principal</span>       <span className="text-right">{fmtUSD(p)}</span>
                <span className="text-slate-600">Opening balance</span> <span className="text-right">{fmtUSD(openPrin)}</span>
                <span className="text-slate-600">Monthly interest (gross) to the Lender</span> <span className="text-right">{fmtUSD(monthly_i)}</span>
                <span className="text-slate-600">WHT withheld ({wht}%)</span>    <span className="text-right">{fmtUSD(monthly_wht)}</span>
              </div>
              {f.interest_accrual_mode === "from_loan_start" && (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 font-medium" data-testid="accrual-amber-note">
                  Interest accrues from loan start date regardless of drawdown timing. Later drawdowns will carry retroactive interest to the loan start date.
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white" data-testid="ln-save">Save (draft)</Button>
        </DialogFooter>
      </DialogContent>
      {/* Phase F.3 (2026-10-11) — Nested EditDayCountDialog for convention change. */}
      {convOpen && initial && (
        <EditDayCountDialog
          loan={{ ...initial, day_count_convention: currentConv }}
          onCancel={() => setConvOpen(false)}
          onSuccess={async () => {
            setConvOpen(false);
            // Refresh the convention from the server so the pill
            // + label reflect the newly-saved value immediately.
            try {
              const r = await api.get(`/loans/${initial.id}`);
              const fresh = r.data?.day_count_convention || currentConv;
              setCurrentConv(fresh);
              toast.success(`Day-count convention updated to ${CONV_LABEL[fresh] || fresh}. Rebuild accruals to re-post interest under the new convention.`);
            } catch {
              // Fallback — the dialog already toasted on success; just leave UI as-is.
            }
          }}
        />
      )}
    </Dialog>
  );
}

function Field({ label, v, on, type = "text", testId, disabled = false }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={v} onChange={(e) => on(e.target.value)}
             disabled={disabled}
             className={`mt-1 ${disabled ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""}`}
             data-testid={testId} />
    </div>
  );
}
function SelField({ label, v, on, options, disabled = false }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={v} onValueChange={on} disabled={disabled}>
        <SelectTrigger className={`mt-1 ${disabled ? "bg-slate-100 text-slate-500" : ""}`}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => {
          const value = typeof o === "string" ? o : o.value;
          const label = typeof o === "string" ? o.replace("_", " ") : o.label;
          return <SelectItem key={value} value={value} data-testid={`loan-freq-opt-${value}`}>{label}</SelectItem>;
        })}</SelectContent>
      </Select>
    </div>
  );
}

function DeleteLoanDialog({ loan, canReverseJEs, onClose, onDone }) {
  // P1 (2026-07-14) — the /loans list endpoint returns a lightweight
  // shape (no schedule) so we use `status === "draft"` as the client-side
  // proxy for "no posted payments". The backend re-checks the schedule
  // and switches to soft-void when a draft actually has je_id rows —
  // so this heuristic can only ever err on the side of MORE confirmation.
  const isHardDelete = loan.status === "draft";
  const [reason, setReason] = useState("");
  const [reverse, setReverse] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!isHardDelete && reason.trim().length < 10) {
      toast.error("Reason must be at least 10 characters"); return;
    }
    setBusy(true);
    try {
      const body = isHardDelete
        ? {}
        : { voided_reason: reason.trim(), reverse_historical_jes: reverse };
      const res = await api.delete(`/loans/${loan.id}`, { data: body });
      if (res.data?.deleted) toast.success(`Loan ${loan.loan_number} deleted.`);
      else toast.success(`Loan ${loan.loan_number} voided. ${res.data?.je_reversals_posted || 0} JE reversal(s) posted.`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-loan-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-700">
            {isHardDelete
              ? `Delete loan ${loan.loan_number}?`
              : `Void loan ${loan.loan_number}?`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1 text-sm">
          {isHardDelete ? (
            <p className="text-slate-600">
              This will permanently delete loan <span className="font-semibold">{loan.loan_number}</span> and its
              amortization schedule. This cannot be undone.
            </p>
          ) : (
            <>
              <p className="text-slate-600">
                This loan is <span className="font-semibold">{loan.status}</span> with posted journal entries.
                Voiding will mark it as <span className="font-semibold">voided</span> and remove it from the Dashboard
                outstanding-loans tile, but the historical journal entries stay intact.
              </p>
              <div>
                <Label className="text-xs uppercase tracking-wider">Reason (min 10 chars)</Label>
                <Textarea
                  value={reason} onChange={(e) => setReason(e.target.value)}
                  rows={3} placeholder="e.g. Superseded by new agreement 0002 dated 2026-07-14"
                  data-testid="void-reason-input"
                />
              </div>
              {canReverseJEs && (
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={reverse}
                          onChange={(e) => setReverse(e.target.checked)}
                          className="mt-0.5 h-4 w-4"
                          data-testid="void-reverse-jes-checkbox" />
                  <span className="text-xs text-slate-600">
                    Also post reversal journal entries for every historical payment
                    (admin only — leaves an audit trail on both sides of the ledger).
                  </span>
                </label>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit} disabled={busy}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="void-confirm-btn"
          >{isHardDelete ? "Delete permanently" : "Void loan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
